import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faInbox } from '@fortawesome/free-solid-svg-icons';
import { Survey, SurveyQuestionType, SurveyResponse } from '../../types';
import { API_BASE_URL } from '../../config/api';

const formatDate = (value?: string | Date | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatAnswer = (value: string, type?: SurveyQuestionType) => {
  if (!value) return '—';
  if (type === 'boolean') {
    if (value === 'yes') return 'Yes';
    if (value === 'no') return 'No';
  }
  return value;
};

const SurveyResultPage: React.FC = () => {
  const { surveyId, resultId } = useParams<{ surveyId: string; resultId: string }>();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [result, setResult] = useState<SurveyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!surveyId || !resultId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/surveys/${surveyId}/results/${resultId}`, {
        withCredentials: true,
      });
      setSurvey(response.data.survey || null);
      setResult(response.data.result || null);
      setError(null);
    } catch (err: any) {
      console.error('Error loading survey result:', err);
      setError(err.response?.data?.message || 'Failed to load survey result');
    } finally {
      setLoading(false);
    }
  }, [resultId, surveyId]);

  useEffect(() => {
    load();
  }, [load]);

  const name = [result?.account?.firstName, result?.account?.lastName].filter(Boolean).join(' ').trim()
    || result?.account?.email
    || 'Not logged in';
  const userId = result?.account?.userId;
  const devices = (result?.deviceSerials || []).join(', ');

  return (
    <div className="container-fluid mt-3 mb-5">
      <div className="mb-3">
        <Link to={surveyId ? `/surveys/${surveyId}/results` : '/surveys'} className="btn btn-sm btn-outline-secondary">
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          All results
        </Link>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '180px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : !result ? (
        <div className="deploy-test-card-empty">This report was not found</div>
      ) : (
        <>
          <div className="d-flex align-items-center gap-2 mb-2">
            <FontAwesomeIcon icon={faInbox} className="text-primary" />
            <h2 className="mb-0">Result{survey ? ` — ${survey.name}` : ''}</h2>
          </div>
          <div className="text-muted mb-4">
            {result.status === 'opted_out' ? 'Opted out' : 'Completed'}
            {result.completedAt ? ` · ${formatDate(result.completedAt)}` : ''}
          </div>

          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between gap-3 mb-4">
                <div>
                  <div className="fw-bold">
                    {userId ? <Link to={`/users/${userId}`}>{name}</Link> : name}
                  </div>
                  {result.account?.email && result.account.email !== name ? (
                    <div className="text-muted small">{result.account.email}</div>
                  ) : null}
                </div>
                <div className="text-muted small text-md-end">
                  <div>
                    Phone {result.phonePlatform === 'ios' ? 'iOS' : result.phonePlatform === 'android' ? 'Android' : '—'}
                  </div>
                  {devices ? <div>Devices {devices}</div> : null}
                </div>
              </div>

              {result.status === 'opted_out' ? (
                <div className="text-muted">This person opted out and did not answer the survey.</div>
              ) : result.questionResponses.length === 0 ? (
                <div className="text-muted">No answers recorded.</div>
              ) : (
                result.questionResponses.map((answer) => (
                  <div key={`${result._id}-${answer.questionId}`} className="mb-3">
                    <div className="fw-bold">{answer.prompt}</div>
                    <div>{formatAnswer(answer.value, answer.type)}</div>
                    {answer.additionalInfo ? (
                      <div className="text-muted small mt-1">{answer.additionalInfo}</div>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SurveyResultPage;
